import { downloadKeys, readDownload, removeDownload, saveDownload } from "@/lib/offline/downloads";
import {
  createOfflinePackRecord,
  type InstalledOfflinePackRecord,
  type OfflinePackManifest,
  type OfflinePackSlug,
} from "@/lib/native/offline-packs";
import { readOfflineJourneyPack, type OfflineJourneyPack } from "@/lib/planner/offline-journey";

export type StoredOfflinePack = InstalledOfflinePackRecord & {
  manifest: OfflinePackManifest;
};

const DB_NAME = "pw-native-offline-packs";
const DB_VERSION = 2;
const PACKS = "packs";
const JOURNEYS = "journeys";
export const MAX_INSTALLED_OFFLINE_JOURNEYS = 8;

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    let blocked = false;
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(PACKS)) {
        db.createObjectStore(PACKS, { keyPath: "slug" });
      }
      if (!db.objectStoreNames.contains(JOURNEYS)) {
        db.createObjectStore(JOURNEYS, { keyPath: "id" });
      }
    };
    req.onsuccess = () => { if (blocked) { req.result.close(); return; } req.result.onversionchange = () => req.result.close(); resolve(req.result); };
    req.onerror = () => reject(req.error);
    req.onblocked = () => { blocked = true; reject(new Error("Close other PeakWave tabs, then try saving the pack again.")); };
  });
}

function tx<T>(
  db: IDBDatabase,
  mode: IDBTransactionMode,
  run: (store: IDBObjectStore) => IDBRequest<T> | void,
  storeName = PACKS,
): Promise<T> {
  return new Promise((resolve, reject) => {
    const t = db.transaction([storeName], mode);
    const store = t.objectStore(storeName);
    let result: T | undefined;
    const req = run(store);
    if (req) req.onsuccess = () => (result = req.result);
    t.oncomplete = () => resolve(result as T);
    t.onerror = () => reject(t.error);
    t.onabort = () => reject(t.error);
  });
}

export async function listInstalledOfflinePacks(): Promise<StoredOfflinePack[]> {
  const db = await openDb();
  const all = await tx<StoredOfflinePack[]>(db, "readonly", (store) => store.getAll());
  db.close();
  return all ?? [];
}

export async function getInstalledOfflinePack(
  slug: OfflinePackSlug,
): Promise<StoredOfflinePack | null> {
  const db = await openDb();
  const pack = await tx<StoredOfflinePack | undefined>(db, "readonly", (store) => store.get(slug));
  db.close();
  return pack ?? null;
}

export async function installOfflinePack(
  manifest: OfflinePackManifest,
  downloadedAtMs = Date.now(),
): Promise<StoredOfflinePack> {
  const record: StoredOfflinePack = {
    ...createOfflinePackRecord(manifest, downloadedAtMs),
    manifest,
  };
  const db = await openDb();
  await tx(db, "readwrite", (store) => {
    store.put(record);
  });
  db.close();
  return record;
}

export async function deleteOfflinePack(slug: OfflinePackSlug): Promise<void> {
  const db = await openDb();
  await tx(db, "readwrite", (store) => {
    store.delete(slug);
  });
  db.close();
}

type StoredJourney = { id: string; raw: string };

/** The same IndexedDB database works in browsers and the native WebView. */
export async function listOfflineJourneys(): Promise<OfflineJourneyPack[]> {
  const db = await openDb();
  try {
    const rows = await tx<StoredJourney[]>(db, "readonly", (store) => store.getAll(undefined, MAX_INSTALLED_OFFLINE_JOURNEYS), JOURNEYS);
    const legacy = rows.flatMap((row) => {
      const pack = typeof row.raw === "string" ? readOfflineJourneyPack(row.raw) : null;
      return pack && pack.id === row.id ? [pack] : [];
    });
    const current = await Promise.all((await downloadKeys("journey")).map(async (key) => {
      const payload = await readDownload(key);
      return payload ? readOfflineJourneyPack(JSON.stringify(payload)) : null;
    }));
    const merged = new Map(legacy.map(pack => [pack.id, pack]));
    for (const pack of current) if (pack) merged.set(pack.id, pack);
    return [...merged.values()].sort((a, b) => b.preparedAt.localeCompare(a.preparedAt));
  } finally { db.close(); }
}

export async function getOfflineJourney(id: string): Promise<OfflineJourneyPack | null> {
  const payload = await readDownload("journey:" + id);
  if (payload) return readOfflineJourneyPack(JSON.stringify(payload));
  const db = await openDb();
  try {
    const row = await tx<StoredJourney | undefined>(db, "readonly", (store) => store.get(id), JOURNEYS);
    const pack = row && typeof row.raw === "string" ? readOfflineJourneyPack(row.raw) : null;
    return pack?.id === id ? pack : null;
  } finally { db.close(); }
}

export async function saveOfflineJourney(pack: OfflineJourneyPack): Promise<void> {
  const safe = readOfflineJourneyPack(JSON.stringify(pack));
  if (!safe) throw new Error("This pack is incomplete or too large to save.");
  await saveDownload("journey:" + safe.id, "journey", safe.areaName + " · Route " + safe.selectedRoute, safe, safe.preparedAt);
}

export async function deleteOfflineJourney(id: string): Promise<void> {
  const db = await openDb();
  try {
    await tx(db, "readwrite", (store) => { store.delete(id); }, JOURNEYS);
    await removeDownload("journey:" + id);
  } finally { db.close(); }
}
